import './PacingTypeCell.scss';

import type { ModalProps } from 'antd';
import { User } from 'mission-control-common-components';
import type { JSX } from 'react';
import React, { memo, useMemo, useState } from 'react';

import EditableDropdown from '../../../../../common/EditableTableCells/EditableDropdown';
import ModalPopup from '../../../../../common/ModalPopup';
import type { PermittedPacingType } from '../../../../../configs';
import { convertConfigToDropdownOption, convertConfigToDropDownOptions, pacingTypes } from '../../../../../configs';
import type { PermissionsNames } from '../../../../../constants';
import { cellPropsComparison } from '../../../../../utils/common';
import { getIdFromTableCell } from '../../columns';
import { usePacingTypeCellMutation } from '../../hooks/usePacingTypeCellMutation';
import useUpdatePacingType from '../../hooks/useUpdatePacingType';
import type { PacingTypeCellProps } from './types';

const PacingTypeCell = (props: PacingTypeCellProps): JSX.Element => {
  const id = getIdFromTableCell(props);
  const {
    value: { pacingType, tierName },
  } = props;

  const [confirmationModalVisible, setConfirmationModalVisible] = useState<boolean>(false);
  const [pacingTypeToSave, setPacingTypeToSave] = useState<string>('');

  const permissions = User.getPermissions<PermissionsNames>();

  const { cellVariant, updatePacingType, loading } = useUpdatePacingType();

  const handleOpenConfirmationModalSetPacingType = (pacingType: PermittedPacingType): void => {
    setPacingTypeToSave(pacingType);
    setConfirmationModalVisible(true);
  };

  const handleCloseConfirmationModal = (): void => {
    setConfirmationModalVisible(false);
    setPacingTypeToSave('');
  };

  const handleChangePacingType = (): void => {
    updatePacingType(id, pacingTypeToSave);
    handleCloseConfirmationModal();
  };

  const confirmationModalProps: ModalProps = useMemo(() => {
    const typeToDescription = (type: string): string => {
      switch (type) {
        case pacingTypes.EVEN.key:
          return 'evenly';
        case pacingTypes.AFAP.key:
          return 'as fast as possible';
        case pacingTypes.YIELD.key:
          return 'based on Yield';
        case pacingTypes.SOV.key:
          return 'based on share of voice';
        default:
          return 'unrecognized';
      }
    };

    const typeToSave = typeToDescription(pacingTypeToSave);
    const currentType = typeToDescription(pacingType.key);

    return {
      title: `Are you sure you want the line item to deliver ${typeToSave} instead of ${currentType}?`,
      okText: 'Yes',
      cancelText: 'No',
      className: 'free-btn',
      cancelButtonProps: { disabled: loading },
      okButtonProps: { loading },
    };
  }, [loading, pacingType.key, pacingTypeToSave]);

  const { isPacingTypeModifiable, permittedPacingTypeOptions } = usePacingTypeCellMutation({
    pacingType,
    tierName,
    permissions,
  });

  return (
    <>
      {isPacingTypeModifiable ? (
        <EditableDropdown<PermittedPacingType>
          selectedValue={convertConfigToDropdownOption<PermittedPacingType>(pacingType)}
          onSubmit={handleOpenConfirmationModalSetPacingType}
          options={convertConfigToDropDownOptions(permittedPacingTypeOptions)}
          variant={cellVariant}
          dataTestId="pacing-type-cell"
          isDisabled={!permissions?.updateLineItem}
        />
      ) : (
        <div className="pacing-type-wrapper">{pacingType.displayName}</div>
      )}
      {confirmationModalVisible && (
        <ModalPopup
          visible={confirmationModalVisible}
          onOk={handleChangePacingType}
          modalProps={confirmationModalProps}
          onCancel={handleCloseConfirmationModal}
        />
      )}
    </>
  );
};

export default memo(PacingTypeCell, cellPropsComparison);
